import type { WatchProgress, WatchlistItem, SmartWatchlistItem, WatchStatus, ThemeColor } from '../types';

const STORAGE_KEYS = {
  WATCH_PROGRESS: 'cinevault_watch_progress',
  WATCHLIST: 'cinevault_watchlist',
  WATCH_HISTORY: 'cinevault_watch_history',
  THEME: 'cinevault_theme',
  PREFERENCES: 'cinevault_preferences',
  SMART_WATCHLIST: 'cinevault_smart_watchlist',
};

// Watch Progress
export const saveWatchProgress = (progress: WatchProgress): void => {
  const allProgress = getWatchProgressMap();
  const key = progress.mediaType === 'tv'
    ? `${progress.mediaType}-${progress.mediaId}-${progress.season}-${progress.episode}`
    : `${progress.mediaType}-${progress.mediaId}`;

  const updated = { ...allProgress, [key]: { ...progress, updatedAt: Date.now() } };
  localStorage.setItem(STORAGE_KEYS.WATCH_PROGRESS, JSON.stringify(updated));
};

export const getWatchProgress = (
  mediaType: 'movie' | 'tv',
  mediaId: number,
  season?: number,
  episode?: number
): WatchProgress | null => {
  const allProgress = getWatchProgressMap();
  const key = mediaType === 'tv'
    ? `${mediaType}-${mediaId}-${season}-${episode}`
    : `${mediaType}-${mediaId}`;

  return allProgress[key] || null;
};

export const getWatchProgressMap = (): Record<string, WatchProgress> => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.WATCH_PROGRESS);
    return data ? JSON.parse(data) : {};
  } catch {
    return {};
  }
};

export const getContinueWatching = (): WatchProgress[] => {
  const allProgress = getWatchProgressMap();
  return Object.values(allProgress)
    .filter(p => p.progress > 5 && p.progress < 95) // Started but not finished
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .slice(0, 20);
};

export const clearWatchProgress = (
  mediaType: 'movie' | 'tv',
  mediaId: number,
  season?: number,
  episode?: number
): void => {
  const allProgress = getWatchProgressMap();
  const key = mediaType === 'tv'
    ? `${mediaType}-${mediaId}-${season}-${episode}`
    : `${mediaType}-${mediaId}`;

  const { [key]: _removed, ...remaining } = allProgress;
  localStorage.setItem(STORAGE_KEYS.WATCH_PROGRESS, JSON.stringify(remaining));
};

// Watchlist
export const addToWatchlist = (item: WatchlistItem): void => {
  const watchlist = getWatchlist();
  const exists = watchlist.some(i => i.id === item.id && i.mediaType === item.mediaType);

  if (!exists) {
    watchlist.unshift({ ...item, addedAt: Date.now() });
    localStorage.setItem(STORAGE_KEYS.WATCHLIST, JSON.stringify(watchlist));
  }
};

export const removeFromWatchlist = (mediaType: 'movie' | 'tv', id: number): void => {
  const watchlist = getWatchlist();
  const filtered = watchlist.filter(i => !(i.id === id && i.mediaType === mediaType));
  localStorage.setItem(STORAGE_KEYS.WATCHLIST, JSON.stringify(filtered));
};

export const getWatchlist = (): WatchlistItem[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.WATCHLIST);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

export const isInWatchlist = (mediaType: 'movie' | 'tv', id: number): boolean => {
  const watchlist = getWatchlist();
  return watchlist.some(i => i.id === id && i.mediaType === mediaType);
};

// Watch History (tracks everything watched)
export const addToHistory = (item: WatchlistItem): void => {
  const history = getWatchHistory();
  // Remove if exists (to move to top)
  const filtered = history.filter(i => !(i.id === item.id && i.mediaType === item.mediaType));
  filtered.unshift({ ...item, addedAt: Date.now() });
  // Keep last 100 items
  const trimmed = filtered.slice(0, 100);
  localStorage.setItem(STORAGE_KEYS.WATCH_HISTORY, JSON.stringify(trimmed));
};

export const getWatchHistory = (): WatchlistItem[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.WATCH_HISTORY);
    return data ? JSON.parse(data) : [];
  } catch {
    return [];
  }
};

// Theme
export const saveTheme = (color: ThemeColor): void => {
  localStorage.setItem(STORAGE_KEYS.THEME, color);
};

export const getTheme = (): ThemeColor => {
  const stored = localStorage.getItem(STORAGE_KEYS.THEME);
  const validColors: ThemeColor[] = ['red', 'blue', 'purple', 'green', 'orange', 'pink'];
  return (stored && validColors.includes(stored as ThemeColor))
    ? stored as ThemeColor
    : 'red';
};

// Preferences
export interface Preferences {
  autoPlay: boolean;
  defaultQuality: string;
  showSpoilers: boolean;
}

export const savePreferences = (prefs: Partial<Preferences>): void => {
  const current = getPreferences();
  const updated = { ...current, ...prefs };
  localStorage.setItem(STORAGE_KEYS.PREFERENCES, JSON.stringify(updated));
};

export const getPreferences = (): Preferences => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.PREFERENCES);
    return data ? JSON.parse(data) : {
      autoPlay: true,
      defaultQuality: 'auto',
      showSpoilers: false,
    };
  } catch {
    return { autoPlay: true, defaultQuality: 'auto', showSpoilers: false };
  }
};

// Stats (Fun feature!)
export const getWatchStats = () => {
  const history = getWatchHistory();
  const progress = Object.values(getWatchProgressMap());

  const moviesWatched = history.filter(h => h.mediaType === 'movie').length;
  const showsWatched = new Set(history.filter(h => h.mediaType === 'tv').map(h => h.id)).size;

  // Estimate total watch time (rough calculation)
  const totalMinutes = progress.reduce((acc, p) => {
    return acc + (p.duration * (p.progress / 100)) / 60;
  }, 0);

  return {
    moviesWatched,
    showsWatched,
    totalMinutes: Math.round(totalMinutes),
    totalHours: Math.round(totalMinutes / 60),
    watchlistSize: getWatchlist().length,
  };
};

// ============================================
// SMART WATCHLIST STORAGE
// ============================================

// Get smart watchlist
export function getSmartWatchlist(): SmartWatchlistItem[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SMART_WATCHLIST);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

// Save smart watchlist
export function saveSmartWatchlist(items: SmartWatchlistItem[]): void {
  localStorage.setItem(STORAGE_KEYS.SMART_WATCHLIST, JSON.stringify(items));
}

// Add item to smart watchlist
export function addToSmartWatchlist(item: SmartWatchlistItem): void {
  const list = getSmartWatchlist();
  const exists = list.find(i => i.tmdbId === item.tmdbId && i.type === item.type);

  if (!exists) {
    list.push({
      ...item,
      addedAt: Date.now(),
      tracking: item.tracking || { enabled: false, notifyMe: false },
    });
    saveSmartWatchlist(list);
  }
}

// Update item in smart watchlist
export function updateSmartWatchlistItem(
  tmdbId: number,
  type: 'movie' | 'tv' | 'anime',
  updates: Partial<SmartWatchlistItem>
): void {
  const list = getSmartWatchlist();
  const index = list.findIndex(i => i.tmdbId === tmdbId && i.type === type);

  if (index !== -1) {
    const updatedList = list.map((item, i) =>
      i === index ? { ...item, ...updates, updatedAt: Date.now() } : item
    );
    saveSmartWatchlist(updatedList);
  }
}

// Remove item from smart watchlist
export function removeFromSmartWatchlist(tmdbId: number, type: 'movie' | 'tv' | 'anime'): void {
  const list = getSmartWatchlist();
  const filtered = list.filter(i => !(i.tmdbId === tmdbId && i.type === type));
  saveSmartWatchlist(filtered);
}

// Get items by status
export function getWatchlistByStatus(status: WatchStatus): SmartWatchlistItem[] {
  return getSmartWatchlist().filter(i => i.status === status);
}

// Get items that need release tracking
export function getTrackedItems(): SmartWatchlistItem[] {
  return getSmartWatchlist().filter(i =>
    i.tracking.enabled &&
    ['caught_up', 'waiting_new_season', 'waiting_new_movie'].includes(i.status)
  );
}

// Check if item is in smart watchlist
export function isInSmartWatchlist(tmdbId: number, type: 'movie' | 'tv' | 'anime'): boolean {
  return getSmartWatchlist().some(i => i.tmdbId === tmdbId && i.type === type);
}

// Update watch progress
export function updateWatchProgress(
  tmdbId: number,
  type: 'movie' | 'tv' | 'anime',
  season?: number,
  episode?: number
): void {
  updateSmartWatchlistItem(tmdbId, type, {
    progress: {
      currentSeason: season,
      currentEpisode: episode,
      lastWatched: new Date().toISOString(),
    },
  });
}

// Mark recap as watched
export function markRecapWatched(tmdbId: number, type: 'movie' | 'tv' | 'anime'): void {
  updateSmartWatchlistItem(tmdbId, type, {
    recap: {
      needed: false,
      lastRecapWatched: new Date().toISOString(),
    },
  });
}
